import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Phone,
  MessageCircle,
  Download,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PRODUCT_MAP, PRODUCTS } from "@/data/products";
import { COMPANY } from "@/data/company";
import { submitEnquiry } from "@/lib/api";
import { onImgError } from "@/lib/images";
import { toast } from "sonner";

export default function ProductDetail({ onOpenEnquiry }) {
  const { slug } = useParams();
  const product = PRODUCT_MAP[slug];

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  if (!product) return <Navigate to="/products" replace />;

  const related = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 3);

  const submitSticky = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      setLoading(true);
      await submitEnquiry({
        ...form,
        product: product.name,
        source: `product-detail-${slug}`,
      });
      toast.success("Enquiry sent! Our team will call you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.overview,
    manufacturer: { "@type": "Organization", name: COMPANY.name },
    image: product.image,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "/" },
      { "@type": "ListItem", position: 2, name: "Products", item: "/products" },
      { "@type": "ListItem", position: 3, name: product.name },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{`${product.name} — Vortex Power Solutions`}</title>
        <meta name="description" content={product.short} />
        <link
          rel="canonical"
          href={`https://vortexpowersolutions.in/products/${slug}`}
        />
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Hero — lighter tint */}
      <section className="bg-vortex-tint text-vortex-navy py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-light opacity-70 pointer-events-none" />
        <div className="container-wide relative">
          <nav
            className="flex items-center gap-2 text-sm text-vortex-slate mb-6"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-vortex-navy transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-vortex-navy transition-colors">
              Products
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-vortex-navy font-semibold">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="section-label mb-5">
                Product
              </div>
              <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-5 text-balance">
                {product.name}
              </h1>
              <p className="text-lg text-vortex-slate max-w-xl mb-6 leading-relaxed">
                {product.short}
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {product.variants.map((v) => (
                  <span
                    key={v}
                    className="text-xs font-semibold text-vortex-navy bg-white border border-vortex-border rounded-full px-3 py-1.5"
                  >
                    {v}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={onOpenEnquiry}
                  className="rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white h-12 px-7 font-semibold"
                  data-testid="pd-quote-btn"
                >
                  Get Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}?text=Hi%20Vortex%2C%20interested%20in%20${encodeURIComponent(product.name)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white h-12 px-7 font-semibold transition-colors"
                  data-testid="pd-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden bg-white border border-vortex-border aspect-square shadow-[0_20px_40px_-15px_rgba(0,65,90,0.15)]">
                <img
                  src={product.image}
                  onError={onImgError}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-20">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-16">
            {/* Overview */}
            <div>
              <div className="section-label mb-4">Overview</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-5">
                Engineered for continuous, critical operation.
              </h2>
              <p className="text-vortex-slate leading-relaxed text-lg">
                {product.overview}
              </p>
            </div>

            {/* Applications */}
            <div>
              <div className="section-label mb-4">Applications</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
                Where it&apos;s used.
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.applications.map((a) => (
                  <div
                    key={a}
                    className="flex items-start gap-3 bg-vortex-alt border border-vortex-border rounded-xl p-4"
                  >
                    <CheckCircle2
                      className="w-5 h-5 text-vortex-blue flex-shrink-0 mt-0.5"
                      strokeWidth={2}
                    />
                    <span className="text-vortex-navy font-medium">{a}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div>
              <div className="section-label mb-4">Features</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
                Built-in technical advantages.
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 border-l-2 border-vortex-blue pl-4 py-1"
                  >
                    <span className="text-vortex-navy">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Specs */}
            <div>
              <div className="section-label mb-4">Technical Specifications</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
                Datasheet at a glance.
              </h2>
              <div className="overflow-hidden rounded-2xl border border-vortex-border">
                <table
                  className="w-full text-left"
                  data-testid={`spec-table-${slug}`}
                >
                  <tbody>
                    {product.specs.map((s, i) => (
                      <tr
                        key={s.label}
                        className={
                          i % 2 === 0 ? "bg-white" : "bg-vortex-alt"
                        }
                      >
                        <th className="p-4 font-semibold text-vortex-navy w-1/3 border-r border-vortex-border">
                          {s.label}
                        </th>
                        <td className="p-4 text-vortex-slate">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-vortex-navy hover:text-vortex-blue transition-colors"
                onClick={() =>
                  toast.info(
                    "Datasheet download will be available shortly. Please request one via the enquiry form.",
                  )
                }
                data-testid="pd-download-btn"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
                Download Product Catlogue (PDF)
              </button>
            </div>

            {/* Advantages */}
            <div>
              <div className="section-label mb-4">Advantages</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
                Why buyers choose it.
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.advantages.map((a) => (
                  <div
                    key={a}
                    className="bg-vortex-navy text-white rounded-xl p-5 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-vortex-blue text-vortex-navy flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <span className="font-semibold">{a}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div>
              <div className="section-label mb-4">FAQ</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
                Answers to common questions.
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {product.faq.map((f, i) => (
                  <AccordionItem
                    value={`item-${i}`}
                    key={i}
                    className="border-vortex-border"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-left font-heading font-semibold text-vortex-navy hover:text-vortex-blue">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-vortex-slate leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Sticky enquiry */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="bg-vortex-navy text-white rounded-3xl p-7 shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
                <div className="relative">
                  <div className="section-label !text-vortex-blue !border-vortex-blue/40 mb-4">
                    Quick Enquiry
                  </div>
                  <h3 className="font-heading font-bold text-2xl mb-5">
                    Get a quote for {product.name}
                  </h3>

                  <form onSubmit={submitSticky} className="space-y-3">
                    <Input
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      required
                      placeholder="Your name"
                      className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 h-11 focus:border-vortex-blue"
                      data-testid="pd-sticky-name"
                    />
                    <Input
                      value={form.phone}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, phone: e.target.value }))
                      }
                      required
                      placeholder="Phone number"
                      className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 h-11 focus:border-vortex-blue"
                      data-testid="pd-sticky-phone"
                    />
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, email: e.target.value }))
                      }
                      placeholder="Email (optional)"
                      className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 h-11 focus:border-vortex-blue"
                      data-testid="pd-sticky-email"
                    />
                    <Textarea
                      value={form.message}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, message: e.target.value }))
                      }
                      rows={3}
                      placeholder="Load rating, application…"
                      className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-vortex-blue"
                      data-testid="pd-sticky-message"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white h-12 font-semibold"
                      data-testid="pd-sticky-submit"
                    >
                      {loading ? "Sending…" : "Request Quote"}
                    </Button>
                  </form>

                  <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-sm">
                    <a
                      href={`tel:${COMPANY.phone}`}
                      className="flex items-center gap-2 hover:text-vortex-blue transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {COMPANY.phone}
                    </a>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="block text-white/60 hover:text-white transition-colors truncate"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      <section className="bg-vortex-alt py-20">
        <div className="container-wide">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="section-label mb-3">Related Products</div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-vortex-navy tracking-tight">
                You might also need.
              </h2>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 font-semibold text-vortex-navy hover:text-vortex-blue"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
              All Products
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
              >
                <Link
                  to={`/products/${p.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden border border-vortex-border hover:shadow-lg transition-all"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      onError={onImgError}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-bold text-lg text-vortex-navy group-hover:text-vortex-blue transition-colors mb-1">
                      {p.name}
                    </h3>
                    <p className="text-sm text-vortex-slate line-clamp-2">
                      {p.short}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
